import { useEffect, useState } from 'react';
import { Globe, Mail, MapPin, Phone } from 'lucide-react';
import {
  useGetSupportPartner,
  useGetSupportPartnerDarkLogo,
  useGetSupportPartnerLogo,
} from '@/api/control/control-plane/control-plane';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { fillSupportTemplate, getLogoVersionParams } from '@/lib/support';

interface PartnerCardProps {
  orgId: string;
}

// Matches the API's one-hour Cache-Control; after that the browser revalidates the logo by ETag.
const LOGO_STALE_TIME = 60 * 60 * 1000;

/**
 * Returns a website URL that is safe to link to (http/https only), or null.
 */
function toSafeUrl(website: string | undefined): string | null {
  if (!website) return null;
  const candidate = /^https?:\/\//i.test(website) ? website : `https://${website}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * Card showing the organization's current partner. Hidden when the org has no partner
 * or when the partner can't be loaded.
 */
export function PartnerCard({ orgId }: PartnerCardProps) {
  const { t } = useLanguage();
  const { data: partnerResponse, isLoading } = useGetSupportPartner(orgId, { query: { retry: false } });
  const partner = partnerResponse?.data ?? null;

  const isDark = useResolvedTheme() === 'dark';

  // Each logo request carries the matching update time(s) as a version, so a replaced logo gets a new
  // query key and a new browser cache entry instead of the cached image. Only the logo for the current
  // theme is fetched; the dark route falls back to the normal logo on the server.
  const { data: lightLogoResponse } = useGetSupportPartnerLogo(
    orgId,
    getLogoVersionParams(partner?.logoUpdatedAt),
    {
      query: { enabled: !isDark && !!partner?.hasLogo, staleTime: LOGO_STALE_TIME, retry: false },
    }
  );
  const { data: darkLogoResponse } = useGetSupportPartnerDarkLogo(
    orgId,
    getLogoVersionParams(partner?.logoUpdatedAt, partner?.darkLogoUpdatedAt),
    {
      query: {
        enabled: isDark && (partner?.hasLogo === true || partner?.hasDarkLogo === true),
        staleTime: LOGO_STALE_TIME,
        retry: false,
      },
    }
  );
  const logoResponse = isDark ? darkLogoResponse : lightLogoResponse;
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const blob = logoResponse?.data;
    if (!(blob instanceof Blob) || blob.size === 0) {
      setLogoUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setLogoUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [logoResponse]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-56" />
        </CardHeader>
      </Card>
    );
  }

  if (!partner) {
    return null;
  }

  const website = toSafeUrl(partner.website);
  const location = [partner.city, partner.stateProvince, partner.country].filter(Boolean).join(', ');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.support.partner.title}</CardTitle>
        <CardDescription>{t.support.partner.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {logoUrl && (
            // Logos are shown in a box of up to 240×80 px (3:1). Wide logos fill the width,
            // square ones the height; partners are asked for 720×240 px with a transparent background.
            <img
              src={logoUrl}
              alt={fillSupportTemplate(t.support.partner.logoAlt, { name: partner.name ?? '' })}
              className="h-20 w-full max-w-60 object-contain object-left"
            />
          )}
          <div className="font-semibold text-lg">{partner.name}</div>
        </div>
        {partner.description && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">{partner.description}</p>
        )}
        <dl className="grid gap-2 text-sm">
          {partner.primaryEmail && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" aria-hidden />
              <dt className="sr-only">{t.support.partner.email}</dt>
              <dd>
                <a className="underline-offset-4 hover:underline" href={`mailto:${partner.primaryEmail}`}>
                  {partner.primaryEmail}
                </a>
              </dd>
            </div>
          )}
          {partner.phoneNumber && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" aria-hidden />
              <dt className="sr-only">{t.support.partner.phone}</dt>
              <dd>
                <a className="underline-offset-4 hover:underline" href={`tel:${partner.phoneNumber.replace(/[^\d+]/g, '')}`}>
                  {partner.phoneNumber}
                </a>
              </dd>
            </div>
          )}
          {website && (
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
              <dt className="sr-only">{t.support.partner.website}</dt>
              <dd>
                <a className="underline-offset-4 hover:underline" href={website} target="_blank" rel="noopener noreferrer">
                  {partner.website}
                </a>
              </dd>
            </div>
          )}
          {location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden />
              <dt className="sr-only">{t.support.partner.location}</dt>
              <dd>{location}</dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}
